import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { ShieldCheck, Clock, Loader2, ArrowLeft, Smartphone } from 'lucide-react';
import { useCustomerAuth } from '../context/CustomerAuthContext';

export default function VerifyOtp() {
  useEffect(() => {
    document.title = 'Verify OTP | Cynos';
  }, []);

  const navigate = useNavigate();
  const { verifyOtp, forgotPassword } = useCustomerAuth();
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [timer, setTimer] = useState(60);
  const [isLoading, setIsLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [success, setSuccess] = useState(false);
  const inputRefs = useRef([]);

  // Get email/phone identifier set by ForgotPassword page
  const identifier = localStorage.getItem('cynos_reset_identifier') || 'your email';
  const deliveryTarget = localStorage.getItem('cynos_reset_delivery_target');
  const resetToken = localStorage.getItem('cynos_reset_token');

  useEffect(() => {
    inputRefs.current[0]?.focus();
  }, []);

  useEffect(() => {
    if (timer > 0) {
      const id = setTimeout(() => setTimer(t => t - 1), 1000);
      return () => clearTimeout(id);
    }
  }, [timer]);

  const handleResend = async () => {
    if (timer > 0) return;
    setResendLoading(true);
    setError('');
    setInfo('');
    try {
      const stored = localStorage.getItem('cynos_reset_identifier');
      if (!stored) {
        setError('Please start password recovery again.');
        setResendLoading(false);
        navigate('/forgot-password', { replace: true });
        return;
      }
      await forgotPassword(stored);
      setOtp(['', '', '', '', '', '']);
      inputRefs.current[0]?.focus();
      setTimer(60);
      setInfo('A new OTP has been sent.');
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || 'Failed to resend OTP. Please try again.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleVerify = useCallback(async () => {
    setIsLoading(true);
    setError('');
    const otpCode = otp.join('');
    
    if (otpCode.length !== 6) {
      setError('Please enter all 6 digits');
      setIsLoading(false);
      return;
    }
    
    if (!resetToken) {
      setError('Reset token missing. Please start password recovery again.');
      setIsLoading(false);
      return;
    }

    try {
      await verifyOtp(otpCode);
      setSuccess(true);
      setTimeout(() => navigate('/reset-password', { replace: true }), 1500);
    } catch (err) {
      setError(err.message || 'Invalid OTP. Please try again.');
      inputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  }, [otp, navigate, verifyOtp, resetToken]);

  const handleOtpChange = useCallback((index, value) => {
    if (value !== '' && !value.match(/\d/)) return;
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    if (value && index < 5) inputRefs.current[index + 1].focus();
    if (newOtp.every(v => v.length === 1)) handleVerify();
  }, [otp, handleVerify]);

  const handleKeyDown = useCallback((e, index) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  }, [otp]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <main className="min-h-screen bg-black pt-24 pb-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center">
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        className="max-w-md w-full space-y-8"
      >
        <motion.div variants={itemVariants}>
          <div className="text-center mb-8">
            <ShieldCheck className="w-16 h-16 mx-auto mb-6 text-cynos-red" />
            <h1 className="font-anton text-4xl md:text-5xl uppercase text-white mb-4">Verify OTP</h1>
            <p className="text-zinc-400">
              Enter the 6-digit code we sent to <strong className="font-mono">{deliveryTarget || identifier}</strong>
            </p>
            {!deliveryTarget && (
              <p className="mt-2 text-xs text-zinc-500">If you signed up with a phone, please check the email associated with your account.</p>
            )}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="bg-cynos-card border border-white/10 rounded-xl p-8">
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-200 text-sm">
              {error}
            </motion.div>
          )}
          {info && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} className="mb-6 p-4 bg-emerald-500/20 border border-emerald-500/50 rounded-lg text-emerald-200 text-sm">
              {info}
            </motion.div>
          )}

          <div className="space-y-6">
            <div className="flex gap-3 justify-center">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  ref={el => { inputRefs.current[index] = el; }}
                  type="text"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleOtpChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-12 h-14 text-2xl font-mono uppercase bg-black/50 border-2 border-white/20 rounded-lg text-center focus:border-cynos-red focus:outline-none transition-colors text-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              ))}
            </div>

            <div className="text-center space-y-2">
              <button
                onClick={handleVerify}
                disabled={isLoading || success}
                className="w-full px-6 py-3 bg-cynos-red text-black font-mono text-xs uppercase tracking-[0.2em] font-semibold rounded-lg hover:bg-cynos-red/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Verifying...
                  </>
                ) : success ? (
                  'Verified!'
                ) : (
                  'Verify OTP'
                )}
              </button>

              <div className="flex items-center gap-2 text-xs text-zinc-500">
                <Clock className="w-4 h-4" />
                <span>Resend in {timer}s</span>
              </div>
            </div>

            <div className="flex items-center justify-center pt-6 border-t border-white/10">
              <button
                onClick={handleResend}
                disabled={resendLoading || timer > 0}
                className="text-cynos-red hover:text-cynos-red/80 font-mono text-xs uppercase tracking-[0.2em] transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {resendLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Resend OTP'
                )}
              </button>
            </div>
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="text-center pt-4">
          <Link to="/profile" className="inline-flex items-center gap-2 text-zinc-500 hover:text-white text-sm font-mono tracking-[0.1em] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Profile
          </Link>
        </motion.div>
      </motion.div>
    </main>
  );
}

